/**
 * useCanvasClickSelection Hook
 * Native pointer event based element selection/deselection.
 * Bypasses JointJS event system entirely — works regardless of SVG view state,
 * hybrid rendering mode, or async paper rendering.
 *
 * Single source of truth for canvas click-based selection/deselection.
 * useCanvasPanning handles drag panning only, NOT click events.
 */

import { useEffect, useRef, RefObject } from 'react'
import { dia } from '@joint/core'
import { EditorTool } from '../../../map-editor-shared/store/uiStore'
import { useZoneDrawingStore } from '../../../map-editor-shared/store/zoneDrawingStore'
import { getFlag } from '../../../config/featureFlags'

export function useCanvasClickSelection(
  paper: dia.Paper | null,
  graph: dia.Graph | null,
  canvasRef: RefObject<HTMLDivElement | null>,
  onSelect: (elementId: string) => void,
  onDeselect: () => void,
  currentTool: EditorTool = null,
  renderReady?: RefObject<boolean>,
  onToggleSelect?: (elementId: string, add: boolean) => void,
  selectedElementIds?: string[],
  canvasHitTest?: (clientX: number, clientY: number) => string | null,
  getCanvasElements?: () => ReadonlyMap<string, { id: string; position: { x: number; y: number }; bounds: { x: number; y: number; width: number; height: number }; zIndex: number }> | null,
  // select-by-layer-modifier: Ctrl/Cmd+click selects all elements with the same data.layer.
  // The hook only detects the modifier and extracts the layer; the actual graph
  // traversal and selection store update is the caller's responsibility.
  // Callable receives the layer string (e.g. "c-cctv") and is expected to replace
  // the current selection with all elements matching that layer.
  onSelectByLayer?: (layer: string) => void
) {
  // Use refs for callbacks to avoid re-registering event handlers on every render
  const onSelectRef = useRef(onSelect)
  const onDeselectRef = useRef(onDeselect)
  const onToggleSelectRef = useRef(onToggleSelect)
  const selectedElementIdsRef = useRef(selectedElementIds)
  const canvasHitTestRef = useRef(canvasHitTest)
  const getCanvasElementsRef = useRef(getCanvasElements)
  const onSelectByLayerRef = useRef(onSelectByLayer)
  onSelectRef.current = onSelect
  onDeselectRef.current = onDeselect
  onToggleSelectRef.current = onToggleSelect
  selectedElementIdsRef.current = selectedElementIds
  canvasHitTestRef.current = canvasHitTest
  getCanvasElementsRef.current = getCanvasElements
  onSelectByLayerRef.current = onSelectByLayer

  // Snapshot modifier state at pointerdown so handlePointerUp can react to the
  // state at the time the press started (matters when the user releases the
  // modifier key before lifting the mouse button).
  const pointerDownRef = useRef<{ x: number; y: number; shiftKey: boolean; ctrlKey: boolean } | null>(null)

  useEffect(() => {
    // CANVAS_EDITOR flag: the Canvas overlay owns all click selection. This
    // hook hit-tests via the (frozen) paper's transform, which no longer
    // matches the visible viewport — its pointerup would spuriously deselect
    // between the overlay's mousedown and mouseup.
    if (getFlag('CANVAS_EDITOR')) return
    const container = canvasRef.current
    if (!paper || !graph || !container) return

    const handlePointerDown = (e: PointerEvent) => {
      // Accept the primary mouse button OR Ctrl/Cmd+click. On macOS, Ctrl+click
      // is reported as button=2 (right-click); we still want it to trigger the
      // select-by-layer modifier rather than be ignored.
      const isModifierClick = e.ctrlKey || e.metaKey
      const isPrimaryClick = e.button === 0
      const isMacCtrlClick = e.button === 2 && isModifierClick
      if (!isPrimaryClick && !isMacCtrlClick) return
      if (currentTool === 'draw') return
      // Zone drawing owns all canvas clicks — never select/deselect objects.
      if (useZoneDrawingStore.getState().mode !== 'idle') return
      pointerDownRef.current = {
        x: e.clientX,
        y: e.clientY,
        shiftKey: e.shiftKey,
        ctrlKey: isModifierClick,
      }
    }

    // Suppress the browser context menu when a modifier is held so Ctrl+click
    // on macOS reaches our pointerup handler instead of being eaten by the
    // OS-level context menu. Plain right-click still works normally.
    const handleContextMenu = (e: MouseEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
      }
    }

    const handlePointerUp = (e: PointerEvent) => {
      if (!pointerDownRef.current) return

      const down = pointerDownRef.current
      pointerDownRef.current = null

      // Only treat as click if pointer didn't move much (5px threshold)
      const dx = Math.abs(e.clientX - down.x)
      const dy = Math.abs(e.clientY - down.y)
      if (dx > 5 || dy > 5) return

      // Convert screen coordinates to paper (local/world) coordinates
      // When renderReady=false (before scaleContentToFit completes), use SVG CTM directly
      // to avoid stale transform matrix in paper.clientToLocalPoint()
      let localPoint: { x: number; y: number }
      if (renderReady?.current === false) {
        const svgEl = (paper as any).svg as SVGSVGElement | undefined
        const ctm = svgEl?.getScreenCTM()
        if (svgEl && ctm) {
          const pt = svgEl.createSVGPoint()
          pt.x = e.clientX
          pt.y = e.clientY
          const transformed = pt.matrixTransform(ctm.inverse())
          localPoint = { x: transformed.x, y: transformed.y }
        } else {
          localPoint = paper.clientToLocalPoint({ x: e.clientX, y: e.clientY })
        }
      } else {
        localPoint = paper.clientToLocalPoint({ x: e.clientX, y: e.clientY })
      }

      // Hit-test: find the topmost element at this position
      // Pass 1: Check SVG-rendered elements via JointJS (uses paper's own coordinate system)
      let hitElementId: string | null = null

      const views = paper.findViewsFromPoint(localPoint)
      if (views.length > 0) {
        // findViewsFromPoint returns views sorted bottom-to-top; last is topmost
        const topView = views[views.length - 1]
        if (topView.model.isElement()) {
          hitElementId = topView.model.id as string
        }
      }

      // Pass 2: Check Canvas2D-rendered elements via hybrid renderer hit-test
      if (!hitElementId && canvasHitTestRef.current) {
        hitElementId = canvasHitTestRef.current(e.clientX, e.clientY)
      }

      // Pass 3: Fallback — manual bbox check on graph elements
      // Covers edge cases where SVG views haven't been created yet (async rendering)
      if (!hitElementId) {
        const elements = graph.getElements()
        let highestZ = -Infinity
        for (const el of elements) {
          const bbox = el.getBBox()
          if (
            localPoint.x >= bbox.x &&
            localPoint.x <= bbox.x + bbox.width &&
            localPoint.y >= bbox.y &&
            localPoint.y <= bbox.y + bbox.height
          ) {
            const z = el.get('z') ?? 0
            if (z >= highestZ) {
              highestZ = z
              hitElementId = el.id as string
            }
          }
        }
      }

      // Pass 4: Canvas-rendered elements bbox check
      // Canvas elements are removed from graph during hybrid rendering,
      // so Pass 3 can't find them. Check them separately.
      if (!hitElementId && getCanvasElementsRef.current) {
        const canvasElements = getCanvasElementsRef.current()
        if (canvasElements) {
          let highestZ = -Infinity
          for (const [id, el] of canvasElements) {
            if (
              localPoint.x >= el.position.x &&
              localPoint.x <= el.position.x + el.bounds.width &&
              localPoint.y >= el.position.y &&
              localPoint.y <= el.position.y + el.bounds.height
            ) {
              if (el.zIndex >= highestZ) {
                highestZ = el.zIndex
                hitElementId = id
              }
            }
          }
        }
      }

      if (hitElementId) {
        // select-by-layer-modifier: Ctrl/Cmd+click → select all elements
        // sharing the clicked element's data.layer. Takes priority over
        // Shift toggle. Falls back to single-select when layer info is
        // missing or is the catch-all 'default'/'unknown' value (which
        // would otherwise sweep up unrelated elements).
        if (down.ctrlKey && onSelectByLayerRef.current) {
          const cell = graph.getCell(hitElementId)
          const layer = cell?.get('data')?.layer as string | undefined
          if (layer && layer !== 'default' && layer !== 'unknown') {
            onSelectByLayerRef.current(layer)
            return
          }
          // fall through to single-select when layer is unusable
        }

        // Shift+click toggles the clicked element in/out of the multi-selection.
        if (down.shiftKey && onToggleSelectRef.current) {
          const isAlreadySelected = selectedElementIdsRef.current?.includes(hitElementId) ?? false
          onToggleSelectRef.current(hitElementId, !isAlreadySelected)
        } else {
          onSelectRef.current(hitElementId)
        }
      } else {
        onDeselectRef.current()
      }
    }

    container.addEventListener('pointerdown', handlePointerDown)
    container.addEventListener('pointerup', handlePointerUp)
    container.addEventListener('contextmenu', handleContextMenu)

    // JointJS intercepts pointer events on SVG elements, preventing them from
    // reaching the container. Listen to JointJS cell:pointerclick directly
    // to handle clicks on SVG-rendered elements.
    const handleCellClick = (cellView: any, evt: any) => {
      if (currentTool === 'draw') return
      // Zone drawing owns all canvas clicks. The SVG cells layer gets
      // pointer-events:none while drawing, but hybridRenderer re-triggers
      // cell:pointerclick programmatically for canvas-rendered elements,
      // so this guard is still required.
      if (useZoneDrawingStore.getState().mode !== 'idle') return
      if (!cellView.model.isElement()) return

      const elementId = cellView.model.id as string

      // select-by-layer-modifier: same Ctrl/Cmd+click priority as the native
      // pointerup path above. Keep the two paths in sync — JointJS intercepts
      // events on SVG elements so both branches must implement the modifier.
      if ((evt.ctrlKey || evt.metaKey) && onSelectByLayerRef.current) {
        const layer = cellView.model.get('data')?.layer as string | undefined
        if (layer && layer !== 'default' && layer !== 'unknown') {
          onSelectByLayerRef.current(layer)
          return
        }
      }

      // Shift+click toggles the clicked element in/out of the multi-selection.
      if (evt.shiftKey && onToggleSelectRef.current) {
        const isAlreadySelected = selectedElementIdsRef.current?.includes(elementId) ?? false
        onToggleSelectRef.current(elementId, !isAlreadySelected)
      } else {
        onSelectRef.current(elementId)
      }
    }

    paper.on('cell:pointerclick', handleCellClick)
    paper.on('cell:pointerdblclick', handleCellClick)

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown)
      container.removeEventListener('pointerup', handlePointerUp)
      container.removeEventListener('contextmenu', handleContextMenu)
      paper.off('cell:pointerclick', handleCellClick)
      paper.off('cell:pointerdblclick', handleCellClick)
    }
  }, [paper, graph, canvasRef, currentTool]) // Callbacks via refs — no dependency needed
}
